import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, Edit } from "@mui/icons-material";
import { getChildren as getChildrenApi, getCurrencies } from "apis";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Child } from "types";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import Flag from "react-world-flags";
import Axios from "axios";

interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  flag: string;
  rate: string;
  baseCurrency: string;
}

interface Props {
  open: any;
  onClose: () => void;
  onBillUpdate: () => void;
}

interface FormFields {
  heading: string;
  ref: string;
  type: string;
  currencyId: string;
  amount: string;
  id: number;
  childId: string;
  billingStartDate: string;
  billingEndDate: string;
}

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const EditBillDialog: React.FC<Props> = ({ open, onClose, onBillUpdate }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [children, setChildren] = useState<Child[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormFields>();

  useEffect(() => {
    if (open) {
      reset({
        heading: open.heading || "",
        ref: open.ref || "",
        type: open.type || "Bill",
        currencyId: open.currencyId || open.currency || "",
        amount: open.amount || "",
        id: open.id,
        childId: open.childId || "",
        billingStartDate: open.billingStartDate || "",
        billingEndDate: open.billingEndDate || "",
      });
    }
  }, [open, reset]);

  useEffect(() => {
    getChildrenApi().then((data) => setChildren(data));
  }, []);

  useEffect(() => {
    getCurrencies().then((data) => setCurrencies(data));
  }, []);

  const onSubmit: SubmitHandler<FormFields> = async (values) => {
    setSubmitting(true);
    try {
      await Axios.put("/api/bill/updates", values);
      enqueueSnackbar(t("bill.updated"), { variant: "success" });
      onBillUpdate();
      onClose();
    } catch {
      enqueueSnackbar(t("bill.update_error"), { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Edit sx={{ fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700}>
            {t("bill.edit")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={t("labels.heading")}
                fullWidth
                size="small"
                {...register("heading", { required: true })}
                error={!!errors.heading}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t("labels.ref")}
                fullWidth
                size="small"
                {...register("ref", { required: true })}
                error={!!errors.ref}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!errors.currencyId}>
                <InputLabel>{t("labels.currency")}</InputLabel>
                <Controller
                  name="currencyId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={t("labels.currency")}
                      sx={inputSx}
                      renderValue={(selected) => {
                        const curr = currencies.find(
                          (c) => c.id === Number(selected),
                        );
                        if (!curr) return null;
                        return (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 22,
                                height: 14,
                                overflow: "hidden",
                                borderRadius: "2px",
                                flexShrink: 0,
                              }}
                            >
                              <Flag code={curr.flag} />
                            </Box>
                            <span>{curr.code}</span>
                            <span style={{ color: "#888" }}>{curr.symbol}</span>
                          </Box>
                        );
                      }}
                    >
                      {currencies.map((currency) => (
                        <MenuItem
                          key={currency.id}
                          value={currency.id}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 22,
                              height: 14,
                              overflow: "hidden",
                              borderRadius: "2px",
                              flexShrink: 0,
                            }}
                          >
                            <Flag code={currency.flag} />
                          </Box>
                          <Box flexGrow={1}>{currency.name}</Box>
                          <Box color="text.secondary">
                            {currency.code} — {currency.symbol}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.currencyId && (
                  <FormHelperText>{t("labels.currency")}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t("labels.amount")}
                fullWidth
                size="small"
                type="number"
                {...register("amount", { required: true })}
                error={!!errors.amount}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={t("labels.startDate")}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...register("billingStartDate")}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={t("labels.endDate")}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...register("billingEndDate")}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!errors.childId}>
                <InputLabel>{t("labels.child")}</InputLabel>
                <Controller
                  name="childId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select {...field} label={t("labels.child")} sx={inputSx}>
                      <MenuItem value="">
                        <em>{t("placeholders.select_child")}</em>
                      </MenuItem>
                      {children.map((child) => (
                        <MenuItem key={child.id} value={child.id}>
                          {`${child.firstName} ${child.lastName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.childId && (
                  <FormHelperText>{t("labels.child")}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!errors.type}>
                <InputLabel>{t("inputs.type")}</InputLabel>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select {...field} label={t("inputs.type")} sx={inputSx}>
                      <MenuItem value="Bill">{t("labels.bill")}</MenuItem>
                      <MenuItem value="Receipt">
                        {t("labels.invoices")}
                      </MenuItem>
                    </Select>
                  )}
                />
                {errors.type && (
                  <FormHelperText>{t("inputs.type")}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: 2 },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("buttons.update")
            )}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default EditBillDialog;
